import { useParams } from '@tanstack/react-router'
import {
  normalizeOrganizationId,
  resolveOrganizationId,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'

/**
 * Route params are untyped while route files are JavaScript, so `$orgId` is narrowed here once.
 * The `$orgId` route parses its param to a number; accept strings too for routes that don't.
 */
export function useRouteOrganizationIdParam(): OrganizationIdInput {
  const params: unknown = useParams({ strict: false })
  const routeOrganizationId =
    typeof params === 'object' && params !== null && 'orgId' in params ? params.orgId : undefined

  return typeof routeOrganizationId === 'number' || typeof routeOrganizationId === 'string'
    ? routeOrganizationId
    : undefined
}

/** The organization a scoped request targets: the explicit id, else the current route's `$orgId`. */
export function useRouteOrganizationId(organizationId?: OrganizationIdInput): number {
  const routeOrganizationId = useRouteOrganizationIdParam()

  return resolveOrganizationId(organizationId, routeOrganizationId)
}

/** The current route's `$orgId`, or undefined outside organization routes (for queries gated on it). */
export function useOptionalRouteOrganizationId(): number | undefined {
  return normalizeOrganizationId(useRouteOrganizationIdParam())
}
